// Typed API layer for the CRM lead-timeline endpoints consumed by the admin SPA
// (Fase 3 — LeadTimeline panel of the operations CRM integration roadmap).
// Thin wrappers over crmRequest so views never inline paths and share one place
// for the timeline contract.

import { crmFetchBlobUrl, crmRequest } from "./crm";
import {
  Comment,
  CreateCommentBody,
  EmailContentUrlResponse,
  EmailHtmlResponse,
  RecordingUrl,
  Timeline,
  TimelineCommentEntry,
  UpdateCommentBody,
} from "./crmTypes";

// A call comment offers recording playback only when it is a call with a
// phoneCallId AND its recording is materialised. `recordingStatus === 'available'`
// is the only state that yields a signed URL (crm PhoneRecordingStatus); 'pending'
// / 'failed' / 'not_recorded' / 'none' / absent must NOT show the play button.
export function canPlayRecording(entry: TimelineCommentEntry): boolean {
  return (
    entry.kind === "llamada" &&
    !!entry.phoneCallId &&
    entry.recordingStatus === "available"
  );
}

// GET /leads/:id/timeline?limit=&before= — one cursor page of the lead's merged
// activity stream (comments, calls, emails, workflow moves, milestones, external
// observations). Accepts hex-32 or Crockford-26 lead id. `before` is the opaque
// `nextCursor` from the previous page; omit it for the first page. A non-existent
// lead yields empty items/pinned (never 404); an invalid id shape is a 400.
export function getLeadTimeline(
  leadUlid: string,
  params: { limit: number; before?: string },
): Promise<Timeline> {
  const qs = new URLSearchParams();
  qs.set("limit", String(params.limit));
  if (params.before) qs.set("before", params.before);
  return crmRequest<Timeline>(
    `/leads/${encodeURIComponent(leadUlid)}/timeline?${qs.toString()}`,
  );
}

// POST /leads/:id/comments -> 201 Comment. Adds an internal note to the lead's
// timeline. `kind` defaults to 'nota_interna' server-side. Requires role
// sales|ops|admin in the CRM (viewer gets 403).
export function createLeadComment(
  leadUlid: string,
  body: CreateCommentBody,
): Promise<Comment> {
  return crmRequest<Comment>(
    `/leads/${encodeURIComponent(leadUlid)}/comments`,
    { method: "POST", body },
  );
}

// PATCH /leads/:id/comments/:commentId -> { ok: true }. Reescribe el cuerpo de
// una nota. Autorización fina en el CRM: solo su AUTOR o un admin (403
// comment_not_owned); una archivada da 409 comment_archived (restaurar antes).
// El cuerpo previo queda en comment_activity_log (historial de versiones).
export function updateLeadComment(
  leadUlid: string,
  commentId: string,
  body: UpdateCommentBody,
): Promise<{ ok: true }> {
  return crmRequest<{ ok: true }>(
    `/leads/${encodeURIComponent(leadUlid)}/comments/${encodeURIComponent(
      commentId,
    )}`,
    { method: "PATCH", body },
  );
}

// DELETE = ARCHIVAR (soft-delete) / POST restore = restaurar. Archivar NUNCA
// borra: la fila y su historial se conservan; la nota sale del timeline del
// equipo y solo la siguen viendo su autor y los admins (marcada, atenuada).
// Misma autorización fina que la edición: autor o admin.
export function setLeadCommentArchived(
  leadUlid: string,
  commentId: string,
  archived: boolean,
): Promise<{ archived: boolean }> {
  const base = `/leads/${encodeURIComponent(
    leadUlid,
  )}/comments/${encodeURIComponent(commentId)}`;
  return archived
    ? crmRequest<{ archived: boolean }>(base, { method: "DELETE" })
    : crmRequest<{ archived: boolean }>(`${base}/restore`, { method: "POST" });
}

// GET /leads/:id/comments/attachments/:attachmentId/download -> objectURL del
// blob (descarga autenticada por cookie; bucket privado GCS). Para pintar las
// imágenes de una nota del timeline y para hidratar el editor al editarla. El
// llamante revoca el objectURL cuando ya no lo necesita.
export function fetchLeadCommentAttachmentObjectUrl(
  leadUlid: string,
  attachmentId: string,
): Promise<string> {
  return crmFetchBlobUrl(
    `/leads/${encodeURIComponent(
      leadUlid,
    )}/comments/attachments/${encodeURIComponent(attachmentId)}/download`,
  );
}

// GET /leads/:id/emails/:emailId/html -> { html: string | null }. Inline HTML
// body of a timeline email, fetched on-demand when the user expands it (the
// timeline list only carries the `hasBodyHtml` flag, never the body — it can be
// several MB). Same auth scope as the timeline itself: requireUser (session
// cookie), NO extra role (crm email.plugin.ts — "es contenido del timeline").
// 404 email_not_found if the email id is unknown for that lead; `{ html: null }`
// (200) when the row has no materialised inline HTML (caller shows "sin cuerpo").
// This endpoint only serves the inline `body_html`; the archived .eml lives on a
// separate content-url endpoint out of scope here.
export function getEmailBodyHtml(
  leadUlid: string,
  emailId: string,
): Promise<EmailHtmlResponse> {
  return crmRequest<EmailHtmlResponse>(
    `/leads/${encodeURIComponent(leadUlid)}/emails/${encodeURIComponent(
      emailId,
    )}/html`,
  );
}

// GET /leads/:id/emails/:emailId/content-url -> EmailContentUrlResponse.
// Segunda fuente del cuerpo: el `.eml` archivado (inbound Gmail / transaccional
// Mailgun). `parsed.html` trae el cuerpo listo para sanitizar+pintar; `url` es
// la URL firmada al original ("descargar .eml"); `attachments` los adjuntos.
export function getEmailContentUrl(
  leadUlid: string,
  emailId: string,
): Promise<EmailContentUrlResponse> {
  return crmRequest<EmailContentUrlResponse>(
    `/leads/${encodeURIComponent(leadUlid)}/emails/${encodeURIComponent(
      emailId,
    )}/content-url`,
  );
}

// GET /phone/calls/:callId/recording-url -> { url, expiresAt, contentType }.
// Short-lived signed URL for playing a call recording. Requires role
// sales|ops|admin.
export function getRecordingUrl(callId: string): Promise<RecordingUrl> {
  return crmRequest<RecordingUrl>(
    `/phone/calls/${encodeURIComponent(callId)}/recording-url`,
  );
}
